import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FileUpload from "@/components/FileUpload";
import { toast } from "sonner";
import { ShieldCheck, Clock } from "lucide-react";

const ID_TYPES = [
  { v: "drivers_licence", l: "Driver's Licence" },
  { v: "passport", l: "Passport" },
  { v: "military_id", l: "Military ID" },
  { v: "proof_of_age", l: "Proof of Age Card" },
];

export default function DriverApplication({ status, onDone }) {
  const [a, setA] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });
  const setVal = (k, v) => setA((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/driver/application").then((r) => setA(r.data.application || {})); }, []);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/driver/application", a); setA(data.application); toast.success("Progress saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.put("/driver/application", a);
      await api.post("/driver/application/submit");
      toast.success("Application submitted for review");
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (status === "submitted") {
    return (
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-700" data-testid="application-under-review">
        <Clock className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-3 font-heading text-xl font-bold text-slate-900">Application under review</h2>
        <p className="mt-1 text-sm text-slate-500">Thanks! Our team is reviewing your documents. You'll be able to accept jobs once you're approved.</p>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 text-slate-900" data-testid="driver-application-form">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-brand" />
        <h2 className="font-heading text-xl font-bold">Complete your driver application</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">We need a few details and documents before you can start accepting jobs.</p>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Date of birth (must be 18+)</Label><Input type="date" value={a.dob || ""} onChange={set("dob")} data-testid="app-dob" /></div>
        <div className="space-y-1.5"><Label>National Insurance number</Label><Input value={a.ni_number || ""} onChange={set("ni_number")} placeholder="QQ123456C" data-testid="app-ni" /></div>
        <div className="space-y-1.5"><Label>Vehicle being used</Label><Input value={a.vehicle_type || ""} onChange={set("vehicle_type")} placeholder="e.g. Car, Van, Motorbike, Bicycle" data-testid="app-vehicle" /></div>
        <div className="space-y-1.5"><Label>Vehicle registration number</Label><Input value={a.vehicle_registration || ""} onChange={set("vehicle_registration")} placeholder="AB12 CDE" data-testid="app-vehicle-reg" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Home address</Label><Textarea rows={2} value={a.address || ""} onChange={set("address")} data-testid="app-address" /></div>
        <div className="space-y-1.5">
          <Label>Photo ID type</Label>
          <Select value={a.photo_id_type || ""} onValueChange={(v) => setVal("photo_id_type", v)}>
            <SelectTrigger data-testid="app-idtype"><SelectValue placeholder="Select ID type" /></SelectTrigger>
            <SelectContent>{ID_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-end"><FileUpload label="Upload Photo ID" value={a.photo_id_file} onChange={(p) => setVal("photo_id_file", p)} testid="app-idfile" /></div>
        <div className="flex items-end"><FileUpload label="Business / vehicle insurance" value={a.insurance_file} onChange={(p) => setVal("insurance_file", p)} testid="app-insurance" /></div>
        <div className="flex items-end"><FileUpload label="Proof of address" value={a.proof_of_address_file} onChange={(p) => setVal("proof_of_address_file", p)} testid="app-proof-address" /></div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 p-4">
        <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={!!a.needs_work_permit} onChange={(e) => setVal("needs_work_permit", e.target.checked)} className="h-4 w-4 accent-[#2563EB]" data-testid="app-needs-permit" /> I require a Work / Study permit for Right to Work</label>
        {a.needs_work_permit && <div className="mt-3"><FileUpload label="Upload Work / Study permit" value={a.work_permit_file} onChange={(p) => setVal("work_permit_file", p)} testid="app-permit-file" /></div>}
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 p-4">
        <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={!!a.is_business} onChange={(e) => setVal("is_business", e.target.checked)} className="h-4 w-4 accent-[#2563EB]" data-testid="app-is-business" /> I operate as a business</label>
        {a.is_business && (
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Company name</Label><Input value={a.company_name || ""} onChange={set("company_name")} data-testid="app-company-name" /></div>
            <div className="space-y-1.5"><Label>Company number</Label><Input value={a.company_number || ""} onChange={set("company_number")} /></div>
            <div className="space-y-1.5"><Label>VAT number</Label><Input value={a.vat_number || ""} onChange={set("vat_number")} /></div>
          </div>
        )}
      </div>

      <label className="mt-6 flex items-start gap-2 text-sm" data-testid="app-tc-wrap">
        <input type="checkbox" checked={!!a.tc_accepted} onChange={(e) => setVal("tc_accepted", e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#2563EB]" data-testid="app-tc" />
        <span>I have read and agree to the <Link to="/terms" target="_blank" className="font-semibold text-brand hover:underline">Terms &amp; Conditions</Link>.</span>
      </label>

      <div className="mt-6 flex gap-3">
        <Button variant="secondary" onClick={save} disabled={saving} data-testid="app-save-btn">Save progress</Button>
        <Button className="bg-brand hover:bg-brand-hover" onClick={submit} disabled={saving} data-testid="app-submit-btn">Submit application</Button>
      </div>
    </div>
  );
}
